// Czy TO konto ma Autopilot — kanał SESYJNY (paczka 12, §H pkt 2).
//
// Grupa nawigacji, kafle Pulpitu i cały panel renderują się wyłącznie na podstawie
// `GET /api/campaigns` → `enabled` / `autopilot_enabled` (`app/campaigns/routes.py`),
// bo tylko ta odpowiedź zna listę beta (`campaigns_allowed()`, `app/campaigns/flags.py`).
// `GET /api/public/flags` (paczka 07, `lib/publicFlags.ts`) zwraca wyłącznie globalne
// boole i dla konta z listy beta powiedziałby „wyłączone” — zostaje kanałem stron bez
// sesji (landing, logowanie) i tu go nie używamy.
//
// Ten sam kształt co `publicFlags.ts`, nie te same bajty: jedna obietnica w locie,
// odpowiedź raz na sesję, DOMYŚLNIE WYŁĄCZONE i wyłączone przy każdym niepowodzeniu
// (sieć, 5xx, 401 — RequireAuth i tak nie wpuści). Lista kampanii pobiera swój stan
// świeżo i przez `primeAutopilotAccess()` odświeża tę pamięć, żeby dwa odczyty w jednej
// sesji nie mogły się rozjechać.

import { useEffect, useState } from 'react'
import { fetchCampaigns, type CampaignsState } from './campaigns'

export interface AutopilotAccess {
  /** Konto może używać kampanii (globalna flaga ALBO lista beta). */
  enabled: boolean
  /** Akcja główna „Autopilot” — druga flaga z tej samej odpowiedzi. */
  autopilotEnabled: boolean
}

export const ACCESS_OFF: AutopilotAccess = Object.freeze({ enabled: false, autopilotEnabled: false })

// Pamięć KARTY (sessionStorage): odświeżenie strony zaczyna od ostatniej odpowiedzi sesji, nie od
// ACCESS_OFF. Bez niej dolny pasek 390 malował „Bibliotekę”, a po odpowiedzi podmieniał ją w tym
// samym miejscu na „Planer” — pod palcem (recenzja 2026-09-17). Odpowiedź serwera i tak przychodzi
// i nadpisuje pamięć; niepowodzenie (sieć, 401) zostawia ostatnią znaną odpowiedź; wylogowanie ją czyści.
const MEMORY_KEY = 'edimi.access.v1'
function remembered(): AutopilotAccess | null {
  try {
    const raw = sessionStorage.getItem(MEMORY_KEY)
    if (!raw) return null
    const a = JSON.parse(raw) as Partial<AutopilotAccess>
    return typeof a.enabled === 'boolean' && typeof a.autopilotEnabled === 'boolean'
      ? { enabled: a.enabled, autopilotEnabled: a.autopilotEnabled }
      : null
  } catch { return null }
}
function remember(a: AutopilotAccess | null): void {
  try {
    if (a) sessionStorage.setItem(MEMORY_KEY, JSON.stringify(a))
    else sessionStorage.removeItem(MEMORY_KEY)
  } catch { /* brak pamięci: do odpowiedzi zostaje ACCESS_OFF, jak dawniej */ }
}

let pending: Promise<AutopilotAccess> | null = null
let pendingState: Promise<CampaignsState> | null = null
let known: AutopilotAccess | null = remembered()
const listeners = new Set<(a: AutopilotAccess) => void>()

/**
 * ONE `GET /api/campaigns` per session, shared by everyone who needs that answer:
 * the access flags below and the shell signal „posty czekają na zgodę”
 * (`hooks/useShellSignals`, which reads `awaiting_consent` on the campaigns).
 * Without this the shell would ask the same endpoint twice on every cold start.
 * A failed read forgets the promise, so the next caller really retries.
 */
export function fetchCampaignsState(): Promise<CampaignsState> {
  if (!pendingState) {
    pendingState = fetchCampaigns().catch((cause) => { pendingState = null; throw cause })
  }
  return pendingState
}

function fromState(state: CampaignsState): AutopilotAccess {
  return { enabled: state.enabled === true, autopilotEnabled: state.enabled === true && state.autopilot_enabled === true }
}

function publish(next: AutopilotAccess, persist = true): void {
  known = next
  if (persist) remember(next)
  for (const fn of listeners) fn(next)
}

export function fetchAutopilotAccess(): Promise<AutopilotAccess> {
  if (!pending) {
    pending = fetchCampaignsState()
      .then((state) => { const a = fromState(state); publish(a); return a })
      // Niepowodzenie (sieć, 5xx, 401) zostawia to, co było znane — pamięć karty albo ACCESS_OFF;
      // inaczej pasek 390 przeskakiwałby na Bibliotekę przy każdej chwilowej awarii sieci
      // (pomiar 2026-09-17). Wygasłą sesję i tak zamyka RequireAuth.
      .catch(() => { const a = known ?? ACCESS_OFF; publish(a, false); return a })
  }
  return pending
}

/** Ekran, który właśnie pobrał `GET /api/campaigns`, oddaje odpowiedź do wspólnej pamięci. */
export function primeAutopilotAccess(state: CampaignsState): void {
  const a = fromState(state)
  pending = Promise.resolve(a)
  pendingState = Promise.resolve(state)
  publish(a)
}

/** Dla testów, ławki i wylogowania: zapomina odpowiedź razem z pamięcią karty. */
export function resetAutopilotAccess(): void {
  pending = null
  pendingState = null
  known = null
  remember(null)
}

/** Dostęp dla tej sesji; `ACCESS_OFF` dopóki (i jeżeli) serwer nie powie inaczej. */
export function useAutopilotAccess(): AutopilotAccess {
  const [access, setAccess] = useState<AutopilotAccess>(known ?? ACCESS_OFF)
  useEffect(() => {
    let alive = true
    listeners.add(setAccess)
    fetchAutopilotAccess().then((a) => { if (alive) setAccess(a) })
    return () => { alive = false; listeners.delete(setAccess) }
  }, [])
  return access
}
